import React, { memo, useMemo } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import DocumentItem from './DocumentItem';

const DocumentSection = memo(({ title, documents, onUpload, onDelete, readOnly = false }) => {
  // Memoize the separation of mandatory and non-mandatory documents
  const { mandatoryDocs, nonMandatoryDocs } = useMemo(() => {
    const mandatory = documents.filter(doc => doc.is_mandatory);
    const nonMandatory = documents.filter(doc => !doc.is_mandatory);
    return { mandatoryDocs: mandatory, nonMandatoryDocs: nonMandatory };
  }, [documents]);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 500,
          color: '#111827',
          fontSize: { xs: '18px', md: '24px' },
          mb: { xs: 2, md: 3 },
        }}
      >
        {title}
      </Typography>
      
      {/* Mandatory Documents Section */}
      {mandatoryDocs.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip 
              label={`${mandatoryDocs.length} required`}
              size="small"
              sx={{
                backgroundColor: '#FEF2F2',
                color: '#EF4444',
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            />
          </Box>
          
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 320px))',
            gap: 2,
            justifyContent: 'start'
          }}>
            {mandatoryDocs.map((doc) => (
              <Box key={doc.id} sx={{ 
                p: 2, 
                border: '1px solid #E5E7EB', 
                borderRadius: 2,
                backgroundColor: '#F9FAFB',
                borderLeft: '4px solid #EF4444'
              }}>
                <DocumentItem 
                  doc={doc} 
                  onUpload={onUpload}
                  onDelete={onDelete}
                  readOnly={readOnly}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Non-Mandatory Documents Section */}
      {nonMandatoryDocs.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip 
              label={`${nonMandatoryDocs.length} optional`}
              size="small"
              sx={{
                backgroundColor: '#F3F4F6',
                color: '#6B7280',
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            />
          </Box>
          
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 320px))',
            gap: 2,
            justifyContent: 'start'
          }}>
            {nonMandatoryDocs.map((doc) => (
              <Box key={doc.id} sx={{ 
                p: 2, 
                border: '1px solid #F3F4F6', 
                borderRadius: 2,
                backgroundColor: 'white'
              }}>
                <DocumentItem 
                  doc={doc} 
                  onUpload={onUpload}
                  onDelete={onDelete}
                  readOnly={readOnly}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
});

DocumentSection.displayName = 'DocumentSection';

export default DocumentSection;
